import React from 'react';
import PhotoPositioner from '../PhotoPositioner';
import { getMediaUrl } from '../../services/api';

function PhotoPicker({
  photoPickerRef,
  photoFileInputRef,
  photoPickerSlot,
  photoPickerPosition,
  allPhotos,
  uploadingPhoto,
  repositioningPhoto,
  onClose,
  onPickerPhotoUpload,
  onPickerAssignPhoto,
  onPickerUnassignPhoto,
  onSetRepositioningPhoto,
  onRepositionSave,
}) {
  if (!photoPickerSlot || !photoPickerPosition) return null;

  return (
    <div
      ref={photoPickerRef}
      className="showcase-photo-picker-overlay"
      style={{ top: photoPickerPosition.top, left: photoPickerPosition.left }}
    >
      <div className="showcase-photo-picker-header">
        <h4>
          {photoPickerSlot === 'headshot'
            ? 'Hero Photos'
            : photoPickerSlot === 'about'
              ? 'About Photos'
              : 'Gallery Photos'}
        </h4>
        <button className="showcase-photo-picker-close" onClick={onClose}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <input
        ref={photoFileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => onPickerPhotoUpload(e.target.files)}
      />

      {allPhotos.length > 0 && (
        <>
          <div className="showcase-photo-picker-label">Choose from library</div>
          <div className="showcase-photo-picker-grid">
            {allPhotos.map((photo) => {
              const isAssignedHere = photo.slot === photoPickerSlot;
              const isAssignedElsewhere = photo.slot && photo.slot !== photoPickerSlot;
              return (
                <div
                  key={photo.id}
                  className={`showcase-photo-picker-thumb${isAssignedHere ? ' selected' : ''}${isAssignedElsewhere ? ' dimmed' : ''}`}
                  onClick={() => {
                    if (isAssignedHere) {
                      onPickerUnassignPhoto(photo.id);
                    } else if (!isAssignedElsewhere) {
                      onPickerAssignPhoto(photo.id);
                    }
                  }}
                >
                  <img src={getMediaUrl(photo.url)} alt="" />
                  {isAssignedHere && (
                    <div className="picker-check">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                  {isAssignedElsewhere && <div className="picker-slot-badge">{photo.slot}</div>}
                </div>
              );
            })}
          </div>
        </>
      )}

      {!repositioningPhoto && allPhotos.filter((p) => p.slot === photoPickerSlot).length > 0 && (
        <div className="picker-reposition-section">
          <div className="showcase-photo-picker-label">Reposition</div>
          <div className="picker-reposition-list">
            {allPhotos
              .filter((p) => p.slot === photoPickerSlot)
              .map((photo) => (
                <button
                  key={photo.id}
                  className="picker-reposition-btn"
                  onClick={() => onSetRepositioningPhoto(photo)}
                >
                  <img src={getMediaUrl(photo.url)} alt="" />
                  <span>Adjust crop</span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="5 9 2 12 5 15" />
                    <polyline points="9 5 12 2 15 5" />
                    <polyline points="15 19 12 22 9 19" />
                    <polyline points="19 9 22 12 19 15" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <line x1="12" y1="2" x2="12" y2="22" />
                  </svg>
                </button>
              ))}
          </div>
        </div>
      )}

      {repositioningPhoto && (
        <PhotoPositioner
          photoUrl={getMediaUrl(repositioningPhoto.url)}
          positionX={repositioningPhoto.position_x ?? 50}
          positionY={repositioningPhoto.position_y ?? 50}
          zoom={repositioningPhoto.zoom ?? 100}
          onSave={onRepositionSave}
          onCancel={() => onSetRepositioningPhoto(null)}
        />
      )}

      {!repositioningPhoto && (
        <div
          className="showcase-photo-picker-upload"
          onClick={() => photoFileInputRef.current?.click()}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#666"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span>{uploadingPhoto ? 'Uploading...' : 'Upload New'}</span>
          <small>Adds to library & this section</small>
        </div>
      )}
    </div>
  );
}

function GalleryCropOverlay({ galleryCropPhoto, galleryCropRect, onSave, onCancel }) {
  if (!galleryCropPhoto || !galleryCropRect) return null;

  return (
    <div
      className="showcase-gallery-crop-overlay"
      style={{
        position: 'absolute',
        top: galleryCropRect.top,
        left: galleryCropRect.left,
        width: galleryCropRect.width,
        zIndex: 2001,
      }}
    >
      <PhotoPositioner
        photoUrl={getMediaUrl(galleryCropPhoto.url)}
        positionX={galleryCropPhoto.position_x ?? 50}
        positionY={galleryCropPhoto.position_y ?? 50}
        zoom={galleryCropPhoto.zoom ?? 100}
        aspectRatio={galleryCropRect.aspectRatio}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
}

export { PhotoPicker, GalleryCropOverlay };
